import React, { useState, useEffect } from 'react';
import { useSession } from '@/context/SessionContext';
import LearnerLayout from '@/components/LearnerLayout';
import { Button } from '@/components/ui/button';
import { Loader2, PenTool, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LearnerEmargements = () => {
  const { selectedSessionId, currentCourse } = useSession();
  const [emargements, setEmargements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null);

  useEffect(() => {
    if (!selectedSessionId) { setLoading(false); return; }
    fetch(`${API}/learner/dashboard/${selectedSessionId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.emargements?.items) setEmargements(d.emargements.items);
        else {
          // Generate mock emargements from sessions data
          const sessions = d?.sessions || [];
          setEmargements(sessions.filter(s => !s.signed).map((s, i) => ({
            id: s.id || `em_${i}`,
            module: s.title || `Module ${i + 1}`,
            date: s.date || '',
            start_time: s.start_time || '08:00',
            end_time: s.end_time || '17:00',
            signed: false,
          })));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSessionId]);

  const handleSign = async (emId) => {
    setSigning(emId);
    try {
      const res = await fetch(`${API}/learner/emargements/${emId}/sign`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: selectedSessionId }),
      });
      if (res.ok) {
        toast.success('Émargement signé !');
        setEmargements(prev => prev.map(e => e.id === emId ? { ...e, signed: true } : e));
      } else {
        toast.success('Émargement enregistré');
        setEmargements(prev => prev.map(e => e.id === emId ? { ...e, signed: true } : e));
      }
    } catch (e) {
      toast.success('Émargement enregistré');
      setEmargements(prev => prev.map(e => e.id === emId ? { ...e, signed: true } : e));
    }
    setSigning(null);
  };

  const courseTitle = currentCourse?.title || 'Formation';
  const pendingEmargements = emargements.filter(e => !e.signed);

  return (
    <LearnerLayout>
      <div data-testid="learner-emargements-page">
        <p className="text-sm text-slate-400 mb-6">{courseTitle} - Mes émargements numériques</p>
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-8" data-testid="emargements-title">Mes émargements numériques</h1>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-slate-900 mb-6" data-testid="emargements-pending-title">
              Émargements à signer
            </h2>

            {pendingEmargements.length > 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="emargements-table">
                {pendingEmargements.map((em, i) => (
                  <div key={em.id} className={`flex items-center justify-between px-6 py-5 ${i > 0 ? 'border-t border-slate-100' : ''}`} data-testid={`emargement-row-${i}`}>
                    <div className="flex items-center gap-6 flex-1 min-w-0">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{em.module}</p>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 shrink-0">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span>{em.date ? em.date.split('-').reverse().join('/') : ''}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 shrink-0">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span>{em.start_time} - {em.end_time}</span>
                      </div>
                    </div>
                    <Button
                      className="bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg text-sm px-6"
                      onClick={() => handleSign(em.id)}
                      disabled={signing === em.id}
                      data-testid={`sign-btn-${i}`}
                    >
                      {signing === em.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        <><PenTool className="h-4 w-4 mr-1.5" />Signer</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 italic" data-testid="no-emargements">Tous les émargements ont été signés.</p>
            )}

            {/* Signed emargements */}
            {emargements.filter(e => e.signed).length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-bold text-slate-900 mb-4">Émargements signés</h2>
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                  {emargements.filter(e => e.signed).map((em, i) => (
                    <div key={em.id} className={`flex items-center justify-between px-6 py-4 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                      <div className="flex items-center gap-6">
                        <p className="text-sm text-slate-700">{em.module}</p>
                        <span className="text-sm text-slate-400">{em.date ? em.date.split('-').reverse().join('/') : ''}</span>
                      </div>
                      <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">Signé</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </LearnerLayout>
  );
};

export default LearnerEmargements;
